/**
 * Task 9.2/§9.3's "SlashCommandAutocomplete" — deferred from Phase 7
 * (`GET /api/plugins/commands` already worked; only the composer UI was
 * missing). Typing `/` at the start of a message shows a filtered list of
 * enabled plugins' slash commands; picking one replaces the composer's
 * text with that command's `prompt` — there is no backend execution path,
 * the prompt is just inserted as the user's own message (CLAUDE.md).
 */
import { useEffect, useState } from "react";
import { pluginsApi } from "../api/client";
import "./SlashCommandAutocomplete.css";

export interface PluginCommand {
  name: string;
  description: string;
  prompt: string;
  plugin: string;
}

interface Props {
  /** The composer's current input value — only shown while it starts with
   * "/" and hasn't advanced past the command token (no space yet). */
  input: string;
  onSelect: (command: PluginCommand) => void;
}

/** Extracts the "/" command query from the composer text, or null if the
 * autocomplete shouldn't be showing (not a leading slash, or the user has
 * moved on to typing the rest of the message after a space). */
export function slashQuery(input: string): string | null {
  const m = input.match(/^\/(\S*)$/);
  return m ? m[1] : null;
}

export default function SlashCommandAutocomplete({ input, onSelect }: Props) {
  const [commands, setCommands] = useState<PluginCommand[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    pluginsApi
      .commands()
      .then((c) => {
        setCommands(c);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const query = slashQuery(input);
  if (query === null || !loaded || commands.length === 0) return null;

  const filtered = commands.filter((c) =>
    c.name.replace(/^\//, "").toLowerCase().startsWith(query.toLowerCase())
  );
  if (filtered.length === 0) return null;

  return (
    <div className="slash-autocomplete" role="listbox">
      {filtered.map((c) => (
        <button
          key={c.name}
          type="button"
          role="option"
          className="slash-autocomplete-item"
          onClick={() => onSelect(c)}
        >
          <span className="slash-autocomplete-name">{c.name}</span>
          <span className="slash-autocomplete-desc dim">{c.description}</span>
          <span className="tag tag-muted">{c.plugin}</span>
        </button>
      ))}
    </div>
  );
}
